import type { Node, Edge } from '../../../core/models/scene';
import { isOrgNodeType } from '../constants/orgNodeTypes';
import { computeOrgReportCounts, computeOrgReportingTargets, isReportsToEdge } from './orgChartReportCounts';

/** Read-only structural fields over the full supplied scene, before collapse/filtering. */
export function buildStructuralFields(nodes: Node[], edges: Edge[]) {
  const isOrg = nodes.some((node) => isOrgNodeType(node.type));
  const ids = new Set(nodes.map((node) => node.id));
  const targets = isOrg ? computeOrgReportingTargets(nodes, edges) : new Map<string, string>();
  const target = (id: string) => targets.get(id) ?? id;
  const parent = new Map<string, string>();
  const children = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (isOrg && !isReportsToEdge(edge)) continue;
    // Atlas uses employee -> manager; other domains retain their directed-graph convention.
    const childId = target(isOrg ? edge.from : edge.to);
    const parentId = target(isOrg ? edge.to : edge.from);
    if (childId === parentId || !ids.has(childId) || !ids.has(parentId)) continue;
    parent.set(childId, parentId);
    const reports = children.get(parentId) ?? new Set<string>();
    reports.add(childId);
    children.set(parentId, reports);
  }
  const counts = isOrg ? computeOrgReportCounts(nodes, edges) : null;
  const levelMap = new Map<string, number>();
  const directReportsMap = new Map<string, number>();
  const totalReportsMap = new Map<string, number>();
  for (const node of nodes) {
    const id = target(node.id);
    const ancestors = new Set([id]);
    let current = parent.get(id);
    while (current && !ancestors.has(current)) {
      ancestors.add(current);
      current = parent.get(current);
    }
    levelMap.set(node.id, ancestors.size - 1);
    if (counts) {
      const count = counts.get(node.id);
      if (count) {
        directReportsMap.set(node.id, count.directReports);
        totalReportsMap.set(node.id, count.totalReports);
      }
    } else {
      const descendants = new Set<string>();
      const stack = [...(children.get(id) ?? [])];
      while (stack.length) {
        const child = stack.pop()!;
        if (child === id || descendants.has(child)) continue;
        descendants.add(child);
        stack.push(...(children.get(child) ?? []));
      }
      directReportsMap.set(node.id, children.get(id)?.size ?? 0);
      totalReportsMap.set(node.id, descendants.size);
    }
  }
  return { levelMap, directReportsMap, totalReportsMap };
}
